import React, { useRef, useEffect } from 'react';
import styles from './Square.module.css';
import { createSquareAriaAttributes } from '../../../utils/accessibilityHelpers';

interface SquareProps {
  /** Square identifier (e.g., "e4") */
  id: string;
  /** Whether this is a light-colored square */
  isLight: boolean;
  /** Whether this square should be highlighted */
  isHighlighted?: boolean;
  /** Type of highlight to apply (for feedback) */
  highlightType?: 'correct' | 'incorrect';
  /** Whether this square is a legal move destination */
  isLegalMove?: boolean;
  /** Whether this square is currently selected */
  isSelected?: boolean;
  /** Whether this square is currently focused via keyboard navigation */
  isFocused?: boolean;
  /** Callback when the square is clicked */
  onClick: () => void;
  /** Callback when a key is pressed on this square */
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  /** Whether to show coordinate label */
  showCoordinate?: boolean;
  /** Children elements (usually a chess piece) */
  children?: React.ReactNode;
  /** Chess piece on this square (for accessibility) */
  piece?: { type: string; color: string } | null;
  /** Additional ARIA attributes for accessibility */
  'aria-label'?: string;
  'aria-selected'?: boolean;
  'aria-current'?: string;
  'role'?: string;
  'tabIndex'?: number;
}

/**
 * Memoized Square - An individual chess board square
 * 
 * Handles rendering and interaction for a single square on the chess board.
 * Can contain a chess piece as a child element. Supports keyboard navigation
 * and accessibility features.
 */
const SquareInternal: React.FC<SquareProps> = ({
  id,
  isLight,
  isHighlighted = false,
  highlightType,
  isLegalMove = false,
  isSelected = false,
  isFocused = false,
  onClick,
  onKeyDown,
  showCoordinate = true,
  children,
  piece,
  ...ariaProps
}) => {
  const squareRef = useRef<HTMLDivElement>(null);

  // Focus management - focus the square when isFocused becomes true
  useEffect(() => {
    if (isFocused && squareRef.current) {
      squareRef.current.focus();
    }
  }, [isFocused]);

  // Handle keyboard events
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    // Handle Enter and Space as click events
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
      return;
    }

    // Pass other keyboard events to parent handler
    if (onKeyDown) {
      onKeyDown(event);
    }
  };

  // Generate accessibility attributes
  const accessibilityAttributes = createSquareAriaAttributes(
    id,
    piece,
    isSelected,
    isHighlighted,
    isLegalMove
  );

  // Fix aria-current type issue
  const ariaCurrent: 'true' | undefined = accessibilityAttributes['aria-current'] === 'true' ? 'true' : undefined;

  const squareClasses = [
    styles.square,
    isLight ? styles.light : styles.dark,
    isHighlighted ? styles.highlighted : '',
    highlightType === 'correct' ? styles.correctFeedback : '',
    highlightType === 'incorrect' ? styles.incorrectFeedback : '',
    isLegalMove ? styles.legalMove : '',
    isSelected ? styles.selected : '',
    isFocused ? styles.focused : '',
  ].filter(Boolean).join(' ');

  const divProps = {
    ref: squareRef,
    className: squareClasses,
    onClick,
    onKeyDown: handleKeyDown,
    'data-testid': `square-${id}`,
    'data-square': id,
    'data-focused': isFocused,
    'aria-label': accessibilityAttributes['aria-label'],
    'aria-selected': accessibilityAttributes['aria-selected'],
    role: accessibilityAttributes.role,
    tabIndex: accessibilityAttributes.tabIndex,
    ...ariaProps,
    ...(isSelected && { 'aria-current': 'true' as const })
  };

  return (
    <div {...(divProps as any)}>
      {children}
      {showCoordinate && <span className={styles.coordinate}>{id}</span>}
    </div>
  );
};

export default React.memo(SquareInternal); 